import React, { useEffect, useState, memo, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "../components/common/SEO";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext";
import ProductCard from "../components/common/ProductCard";
import CONFIG from "../config";
import { getToken } from "../utils/auth";

import { useBranding } from "../context/BrandingContext";
import BRANDING_FALLBACK from "../branding";
import { Helmet } from "react-helmet-async";

const Home = memo(function Home() {
  useCurrency();
  const { branding } = useBranding();
  const BRANDING = branding || BRANDING_FALLBACK;
  const homeContent = branding?.homeContent;
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const lang = isArabic ? 'ar' : 'en';
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [latestReviews, setLatestReviews] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, reviewsRes, filtersRes, heroRes] = await Promise.all([
          axios.get(`${CONFIG.API_URL}/api/products/top-rated`, {
            headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {}
          }),
          axios.get(`${CONFIG.API_URL}/api/reviews/latest`),
          axios.get(`${CONFIG.API_URL}/api/categories`),
          axios.get(`${CONFIG.API_URL}/api/hero`)
        ]);

        const pArr = Array.isArray(productsRes.data) ? productsRes.data :
          Array.isArray(productsRes.data?.data) ? productsRes.data.data : [];
        setTopRated(pArr);

        const reviews = Array.isArray(reviewsRes.data) ? reviewsRes.data :
          Array.isArray(reviewsRes.data?.data) ? reviewsRes.data.data : [];
        setLatestReviews(reviews);

        // Featured Categories only
        const allCats = filtersRes.data || [];
        setCategories(allCats.filter(c => c.is_featured === 1));

        setHeroSlides(heroRes.data || []);
      } catch (err) {
        console.error("Failed to load homepage content.", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  const categoriesList = useMemo(() => {
    const defaultIcons = {
      "Bedrooms": "fa-bed", "Living Rooms": "fa-couch", "Dining": "fa-chair", "Office": "fa-briefcase", "Tables": "fa-table"
    };
    const defaultImgs = {
      "Bedrooms": "https://images.unsplash.com/photo-1505691938895-1758d7eaa511?auto=format&fit=crop&q=80&w=800",
      "Living Rooms": "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&q=80&w=800",
      "Dining": "https://images.unsplash.com/photo-1617806118233-f8e137453f5c?auto=format&fit=crop&q=80&w=800",
      "Office": "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&q=80&w=800",
      "Tables": "https://images.unsplash.com/photo-1533090161767-e6ffed986c88?auto=format&fit=crop&q=80&w=800"
    };

    return categories.map(c => ({
      id: c.id,
      label: isArabic && c.name_ar ? c.name_ar : c.name,
      icon: c.icon || defaultIcons[c.name] || "fa-couch",
      img: c.image_url || defaultImgs[c.name] || "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&q=80&w=800"
    }));
  }, [categories, isArabic]);

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (heroSlides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      }, 8000);
      return () => clearInterval(timer);
    }
  }, [heroSlides]);

  const categoryRef = React.useRef(null);

  const scrollCategories = (direction) => {
    if (categoryRef.current) {
      const { scrollLeft, clientWidth } = categoryRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      categoryRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
      </Helmet>
      <SEO />

      {/* HERO SECTION */}
      <section className="relative h-[85vh] lg:h-[90vh] flex items-center overflow-hidden bg-gray-900 pt-20">

        {/* Background Images and Gradients */}
        {heroSlides.map((slide, idx) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${idx === currentSlide ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <img
              src={slide.image_url}
              alt={slide.title}
              className="w-full h-full object-cover opacity-60 scale-105 animate-slow-zoom"
            />
            <div className={`absolute inset-0 bg-gradient-to-${isArabic ? 'l' : 'r'} from-black/80 via-black/40 to-transparent`} />
          </div>
        ))}

        {/* Text Content - Above all backgrounds */}
        {heroSlides.map((slide, idx) => (
          <div
            key={`text-${slide.id}`}
            className={`absolute inset-0 z-10 transition-opacity duration-1000 ease-in-out ${idx === currentSlide ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full h-full flex items-center text-white">
              <div className={`max-w-2xl ${isArabic ? 'mr-0' : 'ml-0'}`}>
                <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-[1.1] animate-fade-in-up">
                  {isArabic && slide.title_ar ? slide.title_ar : slide.title}
                </h1>
                <p className="text-lg lg:text-xl text-gray-300 mb-10 leading-relaxed max-w-lg animate-fade-in-up delay-200">
                  {isArabic && slide.subtitle_ar ? slide.subtitle_ar : slide.subtitle}
                </p>
                <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
                  <Link to={slide.button_link || "/shop"} className="bg-brand-primary text-white px-10 py-5 font-black text-sm uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-2xl shadow-brand-primary/20" style={{ borderRadius: 'var(--button-radius, 1rem)' }}>
                    {isArabic && slide.button_text_ar ? slide.button_text_ar : (slide.button_text || t('home.browse'))}
                  </Link>
                  <Link to="/contact" className="backdrop-blur-md px-10 py-5 font-black text-sm uppercase tracking-widest transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--button-radius, 1rem)' }}>
                    {t('home.hero_contact')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}


        {/* Carousel Indicators */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-2 rounded-full transition-all ${i === currentSlide ? "w-12 shadow-lg shadow-brand-primary/40" : "w-4 hover:bg-white/50"}`}
                style={{ backgroundColor: i === currentSlide ? 'var(--primary)' : 'rgba(255,255,255,0.3)' }}
              />
            ))}
          </div>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="py-24 px-6 lg:px-8 max-w-7xl mx-auto relative group/cats">
        <div className="text-center mb-16">
          <h2 className="text-xs font-black uppercase tracking-[0.2em] text-brand-primary mb-3">
            {isArabic ? homeContent?.exploreCollections?.ar : homeContent?.exploreCollections?.en}
            {!(isArabic ? homeContent?.exploreCollections?.ar : homeContent?.exploreCollections?.en) && t('home.explore_collections', 'Explore Collections')}
          </h2>
          <h3 className="text-4xl lg:text-5xl font-black tracking-tight" style={{ color: 'var(--text-header)' }}>
            {isArabic ? homeContent?.shopByCategory?.ar : homeContent?.shopByCategory?.en}
            {!(isArabic ? homeContent?.shopByCategory?.ar : homeContent?.shopByCategory?.en) && t('home.categories_title')}
          </h3>
        </div>

        {/* Scroll Controls */}
        {categoriesList.length > 4 && (
          <>
            <button
              onClick={() => scrollCategories('left')}
              className={`absolute left-0 lg:-left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all opacity-0 group-hover/cats:opacity-100 hidden lg:flex`}
              style={{ backgroundColor: 'var(--bg-header)', color: 'var(--text-header)' }}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              onClick={() => scrollCategories('right')}
              className={`absolute right-0 lg:-right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all opacity-0 group-hover/cats:opacity-100 hidden lg:flex`}
              style={{ backgroundColor: 'var(--bg-header)', color: 'var(--text-header)' }}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </>
        )}

        <div
          ref={categoryRef}
          className={`flex overflow-x-auto scrollbar-hide snap-x snap-mandatory gap-8 pb-4 ${categoriesList.length <= 4 ? 'lg:grid lg:grid-cols-4' : ''}`}
        >
          {categoriesList.map((c, i) => (
            <Link
              key={i}
              to={`/shop?category=${c.id}`}
              className="group relative h-[450px] min-w-[300px] lg:min-w-0 lg:w-full flex-shrink-0 snap-center rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
            >
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent group-hover:from-brand-primary/90 transition-colors duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <i className={`fas ${c.icon} text-3xl mb-4 text-brand-primary/50 group-hover:text-white transition-colors`}></i>
                <h4 className="text-2xl font-black mb-2">{c.label}</h4>
                <span className="text-xs font-bold uppercase tracking-widest group-hover:text-white transition-colors" style={{ color: 'var(--text-muted)' }}>
                  {t('home.view_items', 'View Items')} <i className={`fas fa-arrow-${isArabic ? 'left' : 'right'} ml-2`}></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>


      {/* TOP RATED PRODUCTS */}
      <section className="bg-brand-bg-body py-24 px-6 lg:px-8 overflow-hidden border-t border-brand-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="text-xs font-black uppercase tracking-[0.2em] text-brand-primary mb-3">
                {isArabic ? homeContent?.eliteSelection?.ar : homeContent?.eliteSelection?.en}
                {!(isArabic ? homeContent?.eliteSelection?.ar : homeContent?.eliteSelection?.en) && t('home.elite_selection', 'Elite Selection')}
              </h2>
              <h3 className="text-4xl lg:text-5xl font-black tracking-tight" style={{ color: 'var(--text-header)' }}>
                {isArabic ? homeContent?.topRatedProducts?.ar : homeContent?.topRatedProducts?.en}
                {!(isArabic ? homeContent?.topRatedProducts?.ar : homeContent?.topRatedProducts?.en) && t('home.top_rated')}
              </h3>
            </div>
            <Link to="/shop" className="text-brand-primary font-black uppercase tracking-widest text-xs hover:gap-3 flex items-center gap-2 transition-all">
              {isArabic ? (homeContent?.viewAllCollection?.ar || "عرض المزيد") : (homeContent?.viewAllCollection?.en || "View All Collection")} <i className={`fas fa-chevron-${isArabic ? 'left' : 'right'}`}></i>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map(n => <div key={n} className="h-96 bg-gray-200 rounded-3xl animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {topRated?.map(p => (
                <ProductCard key={p.id} product={p} isAr={isArabic} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-xs font-black uppercase tracking-[0.2em] text-brand-primary mb-3">
            {isArabic ? homeContent?.testimonials?.ar : homeContent?.testimonials?.en}
            {!(isArabic ? homeContent?.testimonials?.ar : homeContent?.testimonials?.en) && t('home.testimonials', 'Testimonials')}
          </h2>
          <h3 className="text-4xl lg:text-5xl font-black tracking-tight" style={{ color: 'var(--text-header)' }}>
            {isArabic ? homeContent?.customerReviews?.ar : homeContent?.customerReviews?.en}
            {!(isArabic ? homeContent?.customerReviews?.ar : homeContent?.customerReviews?.en) && t('home.customer_reviews')}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {latestReviews?.map(r => (
            <div
              key={r.id}
              className="bg-brand-bg-header p-10 transition-all duration-500 relative group"
              style={{
                borderRadius: 'var(--card-radius)',
                border: '1px solid var(--border)',
                boxShadow: 'var(--card-shadow)'
              }}
            >
              <span className={isArabic
                ? "absolute top-10 left-10 text-6xl opacity-10 group-hover:text-brand-primary/20 transition-colors pointer-events-none"
                : "absolute top-10 right-10 text-6xl opacity-10 group-hover:text-brand-primary/20 transition-colors pointer-events-none"
              }>
                {isArabic
                  ? (<i className="fas fa-quote-right"></i>)
                  : (<i className="fas fa-quote-left"></i>)
                }
              </span>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-brand-primary/10 text-brand-primary rounded-full flex items-center justify-center font-black text-xl">

                  {isArabic
                    ? (r.name ? r.name.charAt(0).toUpperCase() : "")
                    : (r.name ? r.name.charAt(0).toUpperCase() : "")
                  }
                </div>
                <div>
                  <h5 className="font-black" style={{ color: 'var(--text-header)' }}>
                    {isArabic ? ` ${r.name}` : r.name}
                  </h5>
                  {r.is_verified && <span className="text-[10px] font-black uppercase text-green-500 tracking-tighter italic flex items-center gap-1">
                    <i className="fas fa-check-circle"></i> {t('home.verified')}
                  </span>}
                </div>
              </div>
              <div className="flex gap-1 text-yellow-400 text-sm mb-4">
                {Array.from({ length: 5 }).map((_, i) => <i key={i} className={`${i < r.rating ? 'fas' : 'far'} fa-star`}></i>)}
              </div>
              <p className="leading-relaxed italic mb-8" style={{ color: 'var(--text-main)' }}>"{r.comment}"</p>
              <Link to={`/shop/${r.product_id}`} className="text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-2" style={{ color: 'var(--primary)' }}>
                {t('home.view_product')} <i className={`fas fa-arrow-${isArabic ? 'left' : 'right'}`}></i>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="mx-6 lg:mx-8 mb-20">
        <div
          className="bg-brand-bg-header rounded-[50px] py-16 px-8 text-center overflow-hidden relative group"
          style={{
            borderRadius: 'var(--card-radius)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--card-shadow)'
          }}
        >
          <div className="absolute inset-0 bg-brand-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <h2 className="text-3xl lg:text-4xl font-black mb-8 max-w-2xl mx-auto leading-tight" style={{ color: 'var(--text-header)' }}>
            {isArabic ? homeContent?.trustBannerTitle?.ar : homeContent?.trustBannerTitle?.en}
            {!(isArabic ? homeContent?.trustBannerTitle?.ar : homeContent?.trustBannerTitle?.en) && t('home.trust_title')}
          </h2>
          <div className="flex flex-wrap justify-center gap-8 lg:gap-16">
            {(() => {
              const sPoints = BRANDING.seo?.sellingPoints?.[lang] || [];
              const features = [
                {
                  label: (isArabic ? homeContent?.trustFeatures?.verifiedReviews?.ar : homeContent?.trustFeatures?.verifiedReviews?.en) || sPoints[0] || t('home.trust_features.verified_reviews'),
                  icon: 'fa-check-circle'
                },
                {
                  label: (isArabic ? homeContent?.trustFeatures?.qualityGuarantee?.ar : homeContent?.trustFeatures?.qualityGuarantee?.en) || sPoints[1] || t('home.trust_features.quality_guarantee'),
                  icon: 'fa-award'
                },
                {
                  label: (isArabic ? homeContent?.trustFeatures?.easyContact?.ar : homeContent?.trustFeatures?.easyContact?.en) || sPoints[2] || t('home.trust_features.easy_contact'),
                  icon: 'fa-headset'
                }
              ];
              return features.map((f, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <i className={`fas ${f.icon}`}></i>
                  </span>
                  <span className="font-bold uppercase tracking-widest text-xs" style={{ color: 'var(--text-muted)' }}>{f.label}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>
    </>
  );
});

export default Home;
